import { Stack } from "@mui/material";
import { Key, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { IconFilterList } from "@/modules/irohana-components/assets/icons";
import { IconSort } from "@/modules/irohana-components/assets/icons/IconSort";
import { SORTING } from "@/modules/irohana-components/constants/sorting";
import { useSorting } from "@/modules/irohana-components/hooks/useSorting";
import { FCC } from "@/modules/irohana-components/types/common";
import { createUrlWithParams, getQueryFromURL } from "@/modules/irohana-components/utils/url";

type TTableSortingProps = {
  name: string;
  disabledValues?: Key[];
};

export const TableSorting: FCC<TTableSortingProps> = ({ name, children, disabledValues }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { sorts } = useSorting();

  const onChange = (v: Key[]) => {
    const query = getQueryFromURL();
    let newSort = v[0].toString();

    if (sorts[0] === newSort) {
      newSort = "";
    }

    const url = createUrlWithParams(location.pathname, { ...query, sorts: newSort || undefined });
    navigate(url);
  };

  const currentSelected = useMemo(() => {
    const found = sorts.find((sort) => sort.startsWith(name));
    if (!found) return undefined;
    return [found];
  }, [sorts]);

  const [selected, setSelected] = useState<Key[]>(currentSelected ?? []);

  useEffect(() => {
    setSelected(currentSelected ?? []);
  }, [currentSelected]);

  // Rotate icon
  const shouldRotateIcon = selected.some((item) => typeof item === "string" && item.includes(SORTING.ASC));

  const handleSort = () => {
    let value = "";

    const hasAsc = selected.some((item) => typeof item === "string" && item.includes(SORTING.ASC));
    const hasDesc = selected.some((item) => typeof item === "string" && item.includes(SORTING.DESC));

    if (hasAsc && !disabledValues?.includes(SORTING.ASC)) {
      value = `${name}|${SORTING.DESC}`;
    } else if (hasDesc && !disabledValues?.includes(SORTING.DESC)) {
      value = "";
    } else {
      value = `${name}|${SORTING.ASC}`;
    }
    setSelected([value]);
    onChange?.([value]);
  };

  return (
    <Stack
      direction="row"
      gap={1.25}
      onClick={handleSort}
      sx={{
        cursor: "pointer",
      }}
    >
      {children}
      {selected.length > 0 ? (
        <IconSort style={shouldRotateIcon ? { transform: "rotate(180deg)" } : undefined} fontSize={24} />
      ) : (
        <IconFilterList fontSize={24} />
      )}
    </Stack>
  );
};
